<template>
  <div
    class="m-skin"
    :class="{
      [`m-skin--${skin}`]: skin,
      [`m-skin--variant-${variant}`]: variant,
    }"
  >
    <slot />
  </div>
</template>

<script>
import skins from './skins'

/**
 * A wrapping component that provides a skin context to set the base CSS variables for:
 *
 * - Heading, excerpts and text colours
 * - Link and button colours
 * - Clickable item background hover colour (⚠️ This is different for each skin)
 * - Border colour, background colour and colour on hover for interactive blocks
 *
 * Please note that this component is 'renderless' (it does not create an element in the DOM).
 *
 * ## Support
 * Not all components currently support skinning, but we're working on adding more.
 * To add skinning support to a component, set its CSS properties to the `--skin-<property>` variables (see `MSkin` for available properties),
 * which will make it take on the skin when wrapped:
 *
 * ```vue
 * <MSkin skin="promo">
 *  <!-- MyComponent will use the promo skin -->
 *  <MyComponent />
 * </MSkin>
 * ```
 *
 * We discourage using `MSkin` internally in components, but in some instances it may be necessary to do so (see `MSiteFooter` for example).
 * When doing so avoid using it as the root element.
 *
 * ## Accessibility
 * These combinations have all been tested for AA contrast accessibility, which we can't guarantee by just setting each property individually.
 * Skinning also enforces brand colour combinations, preventing people going off piste with colour combos.
 *
 * ## FAQs
 *
 * ### Why not use props?
 *
 * If every component has a skin prop, that is many props to manage and keep in sync.
 * `MSkin` gives us a single mechanism to control skins independently.
 * This also allows nesting of different skins, [allowing sub-brands like FirstVet](https://manypets.com/uk/first-vet/) on the page without changing any components
 *
 * It also removes the need to pass the skin to each component within.
 * A prop would have to be passed to header, text, button, etc. `MSkin` as a context massively simplifies this.
 *
 * ### Why not use utility classes?
 *
 * Using a component rather than utility classes allows us to refine, change out and deprecate the skins easily without updates to other components,
 * giving us much tighter control.
 *
 * ### Do we want components to override the styling of its children?
 * In short, yes. The benefits we gain from `MSkin` in power and flexibility make it our preferred solution for managing a large library of components that can support multiple skin types in a variety of contexts. Inheritance is also a feature of regular CSS, especially for text properties.
 */
export default {
  name: 'MSkin',
  props: {
    /**
     * Skin to apply, name based on background colour.
     * light, medium, dark, promo (mid and grey deprecated)
     */
    skin: {
      type: String,
      default: 'light',
      validator: (prop) => {
        return skins.includes(prop)
      },
    },
    /**
     * Variant to apply, name based on background colour.
     * Variants 1-5 available, currently medium skin only
     */
    variant: {
      type: String,
      default: '',
      validator: (prop) => {
        return ['', '1', '2', '3', '4', '5'].includes(prop)
      },
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin skin-variants($skin) {
  @for $variant from 1 through 5 {
    &.m-skin--variant-#{$variant} {
      --skin-background: var(
        --color-backgrounds-#{$skin}-#{$variant},
        var(--skin-background-#{$skin})
      );
      --skin-background-hover: var(
        --color-backgrounds-#{$skin}-#{$variant}-hover,
        var(--skin-background-hover-#{$skin})
      );
    }
  }
}

.m-skin {
  display: contents;

  &--light {
    // Background/text/headings
    --skin-background: var(--skin-background-light);
    --skin-background-hover: var(--skin-background-hover-light);
    --skin-color: var(--skin-color-light);
    --skin-excerpt-color: var(--color-text-excerpt-dark);
    --skin-heading-color: var(--skin-heading-color-light);

    // Links and link hover states
    --skin-link-color: var(--color-link-resting-dark);
    --skin-link-color-hover: var(--color-link-hover-dark);

    // Non-link clickable elements
    --skin-clickable-color: var(--skin-color);
    --skin-clickable-background-hover: var(
      --color-clickable-background-hover-light
    );

    // Card borders and content separators
    --skin-card-border-color: var(--border-card);
    --skin-separator-color: var(--border-separator);

    @include skin-variants('light');
  }

  &--medium {
    // Background/text/headings
    --skin-background: var(--skin-background-mid);
    --skin-background-hover: var(--skin-background-hover-mid);
    --skin-color: var(--skin-color-mid);
    --skin-excerpt-color: var(--color-text-excerpt-dark);
    --skin-heading-color: var(--skin-heading-color-mid);

    // Links and link hover states
    --skin-link-color: var(--color-link-resting-dark);
    --skin-link-color-hover: var(--color-link-hover-dark);

    // Non-link clickable elements
    --skin-clickable-color: var(--skin-color);
    --skin-clickable-background-hover: var(
      --color-clickable-background-hover-medium
    );

    // Card borders and content separators
    --skin-card-border-color: var(--border-card);
    --skin-separator-color: var(--border-separator);

    @include skin-variants('medium');
  }

  &--dark {
    // Background/text/headings
    --skin-background: var(--skin-background-dark);
    --skin-background-hover: var(--skin-background-hover-dark);
    --skin-color: var(--skin-color-dark);
    --skin-excerpt-color: var(--skin-color-dark); // inherit
    --skin-heading-color: var(--skin-heading-color-dark);

    // Links and link hover states
    --skin-link-color: var(--color-link-resting-light);
    --skin-link-color-hover: var(--color-link-hover-light);

    // Non-link clickable elements
    --skin-clickable-color: var(--skin-color);
    --skin-clickable-background-hover: var(
      --color-clickable-background-hover-dark
    );

    // Card borders and content separators
    --skin-card-border-color: transparent;
    --skin-separator-color: var(--color-primary-regular);

    @include skin-variants('dark');
  }

  &--promo {
    // Background/text/headings
    --skin-background: var(--skin-background-promo);
    --skin-background-hover: var(--skin-background-hover-promo);
    --skin-color: var(--skin-color-promo);
    --skin-excerpt-color: var(--color-text-excerpt-dark);
    --skin-heading-color: var(--skin-heading-color-promo);

    // Links and link hover states
    --skin-link-color: var(--skin-color-promo);
    --skin-link-color-hover: var(--skin-color-promo);

    // Non-link clickable elements
    --skin-clickable-color: var(--skin-color);
    --skin-clickable-background-hover: var(
      --color-clickable-background-hover-medium
    );

    // Card borders and content separators
    --skin-card-border-color: transparent;
    --skin-separator-color: var(--border-separator);

    @include skin-variants('promo');
  }
}
</style>
